/**
 * Development mode debugging tools for troubleshooting ParrotMate initialization
 * Provides interactive debugging utilities and diagnostic tools
 */

import debugLogger from './debugLogger.js';
import errorReporting from './consolidatedErrorHandling.js';
import performanceMonitor from './performanceMonitor.js';

class DevelopmentTools {
  constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development';
    this.isEnabled = this.isDevelopment || localStorage.getItem('parrotmate_dev_tools') === 'true';
    this.tools = new Map();
    this.diagnostics = new Map();
    
    // Always initialize tools, but only create global interface if enabled
    this.registerTools();
    
    if (this.isEnabled) {
      this.initializeDevelopmentTools();
    }
  }

  /**
   * Initialize development tools
   */
  initializeDevelopmentTools() {
    this.createGlobalInterface();
    this.setupKeyboardShortcuts();
    
    debugLogger.info('Development tools initialized', {
      isEnabled: this.isEnabled,
      toolsCount: this.tools.size
    });
  }

  /**
   * Register all development tools
   */
  registerTools() {
    // Initialization diagnostics
    this.registerTool('initDiagnostics', {
      name: 'Initialization Diagnostics',
      description: 'Run comprehensive initialization diagnostics',
      category: 'diagnostics',
      execute: () => this.runInitializationDiagnostics()
    });

    // API testing tools
    this.registerTool('testApiEndpoint', {
      name: 'Test API Endpoint',
      description: 'Test specific API endpoint with custom parameters',
      category: 'api',
      execute: (endpoint, options = {}) => this.testApiEndpoint(endpoint, options)
    });

    // Performance analysis
    this.registerTool('analyzePerformance', {
      name: 'Analyze Performance',
      description: 'Analyze current performance metrics and identify bottlenecks',
      category: 'performance',
      execute: () => this.analyzePerformance()
    });

    // State inspection
    this.registerTool('inspectState', {
      name: 'Inspect Component State',
      description: 'Inspect and modify component state for debugging',
      category: 'state',
      execute: (component) => this.inspectComponentState(component)
    });

    // Error simulation
    this.registerTool('simulateError', {
      name: 'Simulate Error',
      description: 'Simulate various error conditions for testing',
      category: 'testing',
      execute: (errorType, context = {}) => this.simulateError(errorType, context)
    });

    // Network simulation
    this.registerTool('simulateNetwork', {
      name: 'Simulate Network Conditions',
      description: 'Simulate various network conditions (slow, offline, etc.)',
      category: 'testing',
      execute: (condition) => this.simulateNetworkCondition(condition)
    });

    // Cache management
    this.registerTool('manageCache', {
      name: 'Cache Management',
      description: 'Inspect and manage application cache',
      category: 'cache',
      execute: (action, key = null) => this.manageCacheData(action, key)
    });

    // Log analysis
    this.registerTool('analyzeLogs', {
      name: 'Analyze Logs',
      description: 'Analyze application logs for patterns and issues',
      category: 'logs',
      execute: (filters = {}) => this.analyzeLogs(filters)
    });

    // Environment info
    this.registerTool('environmentInfo', {
      name: 'Environment Information',
      description: 'Display comprehensive environment and system information',
      category: 'system',
      execute: () => this.getEnvironmentInfo()
    });

    // Wizard flow testing
    this.registerTool('testWizardFlow', {
      name: 'Test Wizard Flow',
      description: 'Test wizard initialization flow with various scenarios',
      category: 'wizard',
      execute: (scenario) => this.testWizardFlow(scenario)
    });

    // Initialization monitoring
    this.registerTool('monitorInitialization', {
      name: 'Monitor Initialization',
      description: 'Real-time monitoring of initialization process with detailed metrics',
      category: 'wizard',
      execute: (parrotId) => this.monitorInitialization(parrotId)
    });

    // Error code lookup
    this.registerTool('lookupErrorCode', {
      name: 'Lookup Error Code',
      description: 'Look up error code details and troubleshooting information',
      category: 'debugging',
      execute: (errorCode) => this.lookupErrorCode(errorCode)
    });

    // Initialization replay
    this.registerTool('replayInitialization', {
      name: 'Replay Initialization',
      description: 'Replay initialization sequence with detailed logging',
      category: 'wizard',
      execute: (parrotId, options = {}) => this.replayInitialization(parrotId, options)
    });
  }

  /**
   * Register a development tool
   */
  registerTool(id, tool) {
    this.tools.set(id, {
      id,
      ...tool,
      registeredAt: Date.now()
    });
  }

  /**
   * Create global interface for development tools
   */
  createGlobalInterface() {
    window.parrotmateDev = {
      // Tool execution
      run: (toolId, ...args) => this.runTool(toolId, ...args),
      list: () => this.listTools(),
      help: (toolId) => this.getToolHelp(toolId),
      
      // Quick access to common tools
      diagnose: () => this.runTool('initDiagnostics'),
      testApi: (endpoint, options) => this.runTool('testApiEndpoint', endpoint, options),
      perf: () => this.runTool('analyzePerformance'),
      logs: (filters) => this.runTool('analyzeLogs', filters),
      env: () => this.runTool('environmentInfo'),
      
      // Initialization-specific tools
      monitor: (parrotId) => this.runTool('monitorInitialization', parrotId),
      stopMonitoring: () => this.stopInitializationMonitoring(),
      replay: (parrotId, options) => this.runTool('replayInitialization', parrotId, options),
      testWizard: (scenario) => this.runTool('testWizardFlow', scenario),
      lookupError: (errorCode) => this.runTool('lookupErrorCode', errorCode),
      
      // Utility functions
      enable: () => this.enableDevelopmentTools(),
      disable: () => this.disableDevelopmentTools(),
      reset: () => this.resetDevelopmentTools(),
      
      // Direct access to underlying systems
      logger: debugLogger,
      errorReporting: errorReporting,
      performanceMonitor: performanceMonitor
    };

    debugLogger.info('Development tools interface created at window.parrotmateDev');
  }

  /**
   * Setup keyboard shortcuts for development tools
   */
  setupKeyboardShortcuts() {
    document.addEventListener('keydown', (e) => {
      // Ctrl+Shift+I - Run initialization diagnostics
      if (e.ctrlKey && e.shiftKey && e.key === 'I') {
        e.preventDefault();
        this.runTool('initDiagnostics');
      }
      
      // Ctrl+Shift+P - Analyze performance
      if (e.ctrlKey && e.shiftKey && e.key === 'P') {
        e.preventDefault();
        this.runTool('analyzePerformance');
      }
      
      // Ctrl+Shift+L - Show logs
      if (e.ctrlKey && e.shiftKey && e.key === 'L') {
        e.preventDefault();
        this.runTool('analyzeLogs');
      }
      
      // Ctrl+Shift+E - Show environment info
      if (e.ctrlKey && e.shiftKey && e.key === 'E') {
        e.preventDefault();
        this.runTool('environmentInfo');
      }
    });
  }

  /**
   * Run a development tool
   */
  async runTool(toolId, ...args) {
    const tool = this.tools.get(toolId);
    if (!tool) {
      console.error(`Development tool '${toolId}' not found`);
      return null;
    }

    debugLogger.info(`Running development tool: ${tool.name}`, {
      toolId,
      args,
      category: 'dev_tools'
    });

    try {
      const startTime = performance.now();
      const result = await tool.execute(...args);
      const duration = performance.now() - startTime;
      
      debugLogger.info(`Development tool completed: ${tool.name}`, {
        toolId,
        duration,
        category: 'dev_tools'
      });

      return result;
    } catch (error) {
      debugLogger.error(`Development tool failed: ${tool.name}`, error, {
        toolId,
        category: 'dev_tools'
      });
      throw error;
    }
  }

  /**
   * List available development tools
   */
  listTools() {
    const toolsByCategory = {};
    
    for (const [id, tool] of this.tools) {
      if (!toolsByCategory[tool.category]) {
        toolsByCategory[tool.category] = [];
      }
      toolsByCategory[tool.category].push({
        id,
        name: tool.name,
        description: tool.description
      });
    }

    console.group('🛠️ Available Development Tools');
    for (const [category, tools] of Object.entries(toolsByCategory)) {
      console.group(`📁 ${category.toUpperCase()}`);
      tools.forEach(tool => {
        console.log(`${tool.id}: ${tool.name} - ${tool.description}`);
      });
      console.groupEnd();
    }
    console.groupEnd();

    return toolsByCategory;
  }

  /**
   * Get help for a specific tool
   */
  getToolHelp(toolId) {
    const tool = this.tools.get(toolId);
    if (!tool) {
      console.error(`Tool '${toolId}' not found`);
      return null;
    }

    console.group(`📖 Help: ${tool.name}`);
    console.log(`Description: ${tool.description}`);
    console.log(`Category: ${tool.category}`);
    console.log(`Usage: parrotmateDev.run('${toolId}', ...args)`);
    console.groupEnd();

    return tool;
  }

  /**
   * Run comprehensive initialization diagnostics
   */
  async runInitializationDiagnostics() {
    console.group('🔍 Initialization Diagnostics');
    
    const diagnostics = {
      timestamp: new Date().toISOString(),
      environment: this.getEnvironmentInfo(),
      performance: this.analyzePerformance(),
      logs: this.analyzeLogs({ category: 'initialization', timeRange: 300000 }), // Last 5 minutes
      errors: errorReporting.getRecentErrors(5),
      apiHealth: await this.checkApiHealth(),
      browserCompatibility: this.checkBrowserCompatibility(),
      localStorage: this.checkLocalStorage(),
      sessionStorage: this.checkSessionStorage(),
      networkConditions: this.checkNetworkConditions()
    };

    // Generate diagnostic summary
    const issues = this.identifyIssues(diagnostics);
    const recommendations = this.generateRecommendations(issues);

    console.log('📊 Diagnostic Results:', diagnostics);
    console.log('⚠️ Issues Found:', issues);
    console.log('💡 Recommendations:', recommendations);
    
    console.groupEnd();

    return {
      diagnostics,
      issues,
      recommendations,
      summary: {
        totalIssues: issues.length,
        criticalIssues: issues.filter(i => i.severity === 'critical').length,
        performanceScore: this.calculatePerformanceScore(diagnostics.performance),
        healthScore: this.calculateHealthScore(diagnostics)
      }
    };
  }

  /**
   * Test API endpoint with custom parameters
   */
  async testApiEndpoint(endpoint, options = {}) {
    console.group(`🌐 Testing API Endpoint: ${endpoint}`);
    
    const testOptions = {
      timeout: 10000,
      retries: 1,
      ...options
    };

    try {
      const startTime = performance.now();
      
      // Import ApiClient dynamically to avoid circular dependencies
      const { default: ApiClient } = await import('./apiClient.js');
      const { getToken } = await import('@clerk/clerk-react');
      
      const apiClient = new ApiClient(getToken);
      const response = await apiClient.get(endpoint, testOptions);
      
      const duration = performance.now() - startTime;
      
      console.log('✅ API Test Successful:', {
        endpoint,
        duration: `${duration.toFixed(2)}ms`,
        response: response,
        options: testOptions
      });

      return {
        success: true,
        duration,
        response,
        endpoint,
        options: testOptions
      };
    } catch (error) {
      console.error('❌ API Test Failed:', {
        endpoint,
        error: error.message,
        status: error.status,
        options: testOptions
      });

      return {
        success: false,
        error: {
          message: error.message,
          status: error.status,
          name: error.name
        },
        endpoint,
        options: testOptions
      };
    } finally {
      console.groupEnd();
    }
  }

  /**
   * Analyze current performance metrics
   */
  analyzePerformance() {
    const summary = performanceMonitor.getPerformanceSummary();
    const detailed = performanceMonitor.getDetailedMetrics();
    
    const analysis = {
      summary,
      detailed,
      issues: [],
      recommendations: []
    };

    // Identify performance issues
    if (summary.averageResponseTime > 3000) {
      analysis.issues.push({
        type: 'slow_response_time',
        severity: 'warning',
        message: `Average response time is ${summary.averageResponseTime.toFixed(2)}ms (threshold: 3000ms)`
      });
    }

    if (summary.errorRate > 5) {
      analysis.issues.push({
        type: 'high_error_rate',
        severity: 'critical',
        message: `Error rate is ${summary.errorRate.toFixed(2)}% (threshold: 5%)`
      });
    }

    if (summary.cacheHitRate < 50) {
      analysis.issues.push({
        type: 'low_cache_hit_rate',
        severity: 'warning',
        message: `Cache hit rate is ${summary.cacheHitRate.toFixed(2)}% (target: >50%)`
      });
    }

    // Generate recommendations
    if (analysis.issues.length > 0) {
      analysis.recommendations.push('Consider optimizing API requests and caching strategies');
    }

    console.group('📈 Performance Analysis');
    console.log('Summary:', summary);
    console.log('Issues:', analysis.issues);
    console.log('Recommendations:', analysis.recommendations);
    console.groupEnd();

    return analysis;
  }

  /**
   * Inspect component state (placeholder for React DevTools integration)
   */
  inspectComponentState(component) {
    console.group(`🔍 Component State: ${component || 'Current'}`);
    
    // This would integrate with React DevTools in a real implementation
    console.log('Note: Component state inspection requires React DevTools integration');
    console.log('Available through browser React DevTools extension');
    
    console.groupEnd();

    return {
      message: 'Use React DevTools browser extension for component state inspection',
      component: component || 'Current'
    };
  }

  /**
   * Simulate various error conditions
   */
  simulateError(errorType, context = {}) {
    console.group(`🧪 Simulating Error: ${errorType}`);
    
    const errorTypes = {
      network: () => {
        const error = new Error('Simulated network error');
        error.name = 'NetworkError';
        return error;
      },
      timeout: () => {
        const error = new Error('Simulated timeout error');
        error.name = 'AbortError';
        return error;
      },
      auth: () => {
        const error = new Error('Simulated authentication error');
        error.status = 401;
        return error;
      },
      server: () => {
        const error = new Error('Simulated server error');
        error.status = 500;
        return error;
      },
      validation: () => {
        const error = new Error('Simulated validation error');
        error.name = 'ValidationError';
        return error;
      }
    };

    const errorGenerator = errorTypes[errorType];
    if (!errorGenerator) {
      console.error(`Unknown error type: ${errorType}`);
      console.log('Available types:', Object.keys(errorTypes));
      console.groupEnd();
      return null;
    }

    const simulatedError = errorGenerator();
    
    // Report the simulated error
    const report = errorReporting.reportError(simulatedError, {
      ...context,
      component: 'DevelopmentTools',
      operation: 'error_simulation',
      simulated: true
    });

    console.log('Simulated Error:', simulatedError);
    console.log('Error Report:', report);
    console.groupEnd();

    return {
      error: simulatedError,
      report
    };
  }

  /**
   * Simulate network conditions
   */
  simulateNetworkCondition(condition) {
    console.group(`🌐 Simulating Network: ${condition}`);
    
    // This would integrate with browser DevTools Network throttling
    console.log('Note: Network simulation requires browser DevTools Network throttling');
    console.log(`Requested condition: ${condition}`);
    console.log('Available in Chrome DevTools > Network > Throttling');
    
    console.groupEnd();

    return {
      condition,
      message: 'Use browser DevTools Network throttling for network simulation'
    };
  }

  /**
   * Manage cache data
   */
  manageCacheData(action, key = null) {
    console.group(`💾 Cache Management: ${action}`);
    
    // Import cache manager dynamically
    import('./cacheManager.js').then(({ default: cacheManager }) => {
      switch (action) {
        case 'stats':
          console.log('Cache Statistics:', cacheManager.getStatistics());
          break;
        case 'clear':
          if (key) {
            cacheManager.delete(key);
            console.log(`Cleared cache key: ${key}`);
          } else {
            cacheManager.clear();
            console.log('Cleared all cache');
          }
          break;
        case 'list':
          console.log('Cache Contents:', cacheManager.getDetailedInfo());
          break;
        default:
          console.log('Available actions: stats, clear, list');
      }
    });
    
    console.groupEnd();

    return { action, key };
  }

  /**
   * Analyze application logs
   */
  analyzeLogs(filters = {}) {
    const logs = debugLogger.getLogs(filters);
    
    const analysis = {
      totalLogs: logs.length,
      logsByLevel: {},
      logsByCategory: {},
      recentErrors: [],
      patterns: []
    };

    logs.forEach(log => {
      // Count by level
      analysis.logsByLevel[log.level] = (analysis.logsByLevel[log.level] || 0) + 1;
      
      // Count by category
      const category = log.context.category || 'uncategorized';
      analysis.logsByCategory[category] = (analysis.logsByCategory[category] || 0) + 1;
      
      // Collect recent errors
      if (log.level === 'error') {
        analysis.recentErrors.push({
          timestamp: log.timestamp,
          message: log.message,
          category: log.context.category
        });
      }
    });

    // Identify patterns
    if (analysis.logsByLevel.error > 5) {
      analysis.patterns.push('High error frequency detected');
    }
    
    if (analysis.logsByCategory.initialization > 10) {
      analysis.patterns.push('Multiple initialization attempts detected');
    }

    console.group('📋 Log Analysis');
    console.log('Analysis:', analysis);
    console.log('Recent Logs:', logs.slice(-10));
    console.groupEnd();

    return analysis;
  }

  /**
   * Get comprehensive environment information
   */
  getEnvironmentInfo() {
    const info = {
      timestamp: new Date().toISOString(),
      environment: {
        nodeEnv: process.env.NODE_ENV,
        isDevelopment: this.isDevelopment,
        buildVersion: process.env.REACT_APP_VERSION || 'unknown'
      },
      browser: {
        userAgent: navigator.userAgent,
        language: navigator.language,
        languages: navigator.languages,
        platform: navigator.platform,
        cookieEnabled: navigator.cookieEnabled,
        onLine: navigator.onLine
      },
      screen: {
        width: screen.width,
        height: screen.height,
        availWidth: screen.availWidth,
        availHeight: screen.availHeight,
        colorDepth: screen.colorDepth,
        pixelDepth: screen.pixelDepth
      },
      window: {
        innerWidth: window.innerWidth,
        innerHeight: window.innerHeight,
        outerWidth: window.outerWidth,
        outerHeight: window.outerHeight,
        devicePixelRatio: window.devicePixelRatio
      },
      location: {
        href: window.location.href,
        protocol: window.location.protocol,
        host: window.location.host,
        pathname: window.location.pathname,
        search: window.location.search,
        hash: window.location.hash
      },
      performance: {
        memory: performance.memory ? {
          usedJSHeapSize: performance.memory.usedJSHeapSize,
          totalJSHeapSize: performance.memory.totalJSHeapSize,
          jsHeapSizeLimit: performance.memory.jsHeapSizeLimit
        } : null,
        timing: performance.timing ? {
          navigationStart: performance.timing.navigationStart,
          loadEventEnd: performance.timing.loadEventEnd,
          domContentLoadedEventEnd: performance.timing.domContentLoadedEventEnd
        } : null
      },
      storage: {
        localStorage: this.checkLocalStorage(),
        sessionStorage: this.checkSessionStorage()
      },
      network: this.checkNetworkConditions()
    };

    console.group('🌍 Environment Information');
    console.log(info);
    console.groupEnd();

    return info;
  }

  /**
   * Test wizard flow with various scenarios
   */
  async testWizardFlow(scenario = 'normal') {
    console.group(`🧙 Testing Wizard Flow: ${scenario}`);
    
    const scenarios = {
      normal: 'Normal initialization flow',
      slow_network: 'Slow network conditions',
      api_error: 'API error during initialization',
      timeout: 'Request timeout scenario',
      auth_error: 'Authentication error scenario',
      cache_miss: 'Cache miss scenario',
      partial_failure: 'Partial data loading failure'
    };

    if (!scenarios[scenario]) {
      console.error(`Unknown scenario: ${scenario}`);
      console.log('Available scenarios:', Object.keys(scenarios));
      console.groupEnd();
      return null;
    }

    console.log(`Testing scenario: ${scenarios[scenario]}`);
    
    const testResult = {
      scenario,
      description: scenarios[scenario],
      startTime: Date.now(),
      steps: [],
      success: false,
      duration: 0,
      errors: [],
      warnings: []
    };

    try {
      // Simulate different scenarios
      switch (scenario) {
        case 'normal':
          testResult.steps = await this.simulateNormalFlow();
          testResult.success = true;
          break;
        case 'slow_network':
          testResult.steps = await this.simulateSlowNetworkFlow();
          testResult.success = true;
          testResult.warnings.push('Slow network conditions detected');
          break;
        case 'api_error':
          testResult.steps = await this.simulateApiErrorFlow();
          testResult.errors.push('API error during data loading');
          break;
        case 'timeout':
          testResult.steps = await this.simulateTimeoutFlow();
          testResult.errors.push('Request timeout occurred');
          break;
        case 'auth_error':
          testResult.steps = await this.simulateAuthErrorFlow();
          testResult.errors.push('Authentication failed');
          break;
        default:
          testResult.warnings.push('Scenario simulation not fully implemented');
      }
    } catch (error) {
      testResult.errors.push(error.message);
    }

    testResult.duration = Date.now() - testResult.startTime;
    
    console.log('Test Result:', testResult);
    console.groupEnd();

    return testResult;
  }

  /**
   * Monitor initialization process in real-time
   */
  async monitorInitialization(parrotId) {
    console.group(`📊 Monitoring Initialization: ${parrotId || 'New Parrot'}`);
    
    const monitor = {
      parrotId: parrotId || 'new',
      startTime: Date.now(),
      steps: [],
      metrics: {
        totalDuration: 0,
        apiCalls: 0,
        cacheHits: 0,
        cacheMisses: 0,
        retries: 0,
        errors: 0
      },
      isActive: true
    };

    // Set up real-time monitoring
    const originalLog = debugLogger.logInitializationStep;
    debugLogger.logInitializationStep = (...args) => {
      const result = originalLog.apply(debugLogger, args);
      
      if (monitor.isActive) {
        const [step, status, duration, metadata] = args;
        monitor.steps.push({
          step,
          status,
          duration,
          timestamp: Date.now(),
          metadata
        });

        // Update metrics
        if (status === 'completed' && duration) {
          monitor.metrics.totalDuration += duration;
        }
        if (metadata?.isApiCall) monitor.metrics.apiCalls++;
        if (metadata?.cacheHit) monitor.metrics.cacheHits++;
        if (metadata?.cacheMiss) monitor.metrics.cacheMisses++;
        if (status === 'retrying') monitor.metrics.retries++;
        if (status === 'failed') monitor.metrics.errors++;

        // Real-time console updates
        console.log(`📈 Step Update: ${step} - ${status}${duration ? ` (${duration.toFixed(2)}ms)` : ''}`);
      }
      
      return result;
    };

    console.log('🔍 Real-time monitoring started. Monitoring will continue until stopped.');
    console.log('Use parrotmateDev.stopMonitoring() to stop monitoring.');
    
    // Store monitor reference for stopping
    this.activeMonitor = monitor;
    
    // Auto-stop after 5 minutes
    setTimeout(() => {
      if (this.activeMonitor === monitor) {
        this.stopInitializationMonitoring();
      }
    }, 5 * 60 * 1000);

    return monitor;
  }

  /**
   * Stop initialization monitoring
   */
  stopInitializationMonitoring() {
    if (this.activeMonitor) {
      this.activeMonitor.isActive = false;
      this.activeMonitor.endTime = Date.now();
      this.activeMonitor.totalMonitoringTime = this.activeMonitor.endTime - this.activeMonitor.startTime;
      
      console.group('📊 Monitoring Summary');
      console.log('Total Monitoring Time:', `${this.activeMonitor.totalMonitoringTime}ms`);
      console.log('Steps Captured:', this.activeMonitor.steps.length);
      console.log('Metrics:', this.activeMonitor.metrics);
      console.log('Step Details:', this.activeMonitor.steps);
      console.groupEnd();
      
      const summary = { ...this.activeMonitor };
      this.activeMonitor = null;
      
      return summary;
    }
    
    console.log('No active monitoring session to stop.');
    return null;
  }

  /**
   * Look up error code details
   */
  lookupErrorCode(errorCode) {
    console.group(`🔍 Error Code Lookup: ${errorCode}`);
    
    if (!errorCode) {
      console.error('Please provide an error code to lookup');
      console.groupEnd();
      return null;
    }

    // Parse error code format: PM-COMPONENT-CATEGORY-TIMESTAMP-RANDOM
    const parts = errorCode.split('-');
    
    const lookup = {
      errorCode,
      isValid: parts.length >= 5 && parts[0] === 'PM',
      parsed: {
        prefix: parts[0],
        component: parts[1],
        category: parts[2],
        timestamp: parts[3],
        random: parts[4]
      },
      componentInfo: this.getComponentInfo(parts[1]),
      categoryInfo: this.getCategoryInfo(parts[2]),
      timestampInfo: this.getTimestampInfo(parts[3]),
      troubleshooting: this.getTroubleshootingInfo(parts[1], parts[2]),
      relatedErrors: this.findRelatedErrors(errorCode)
    };

    console.log('Lookup Result:', lookup);
    
    if (lookup.isValid) {
      console.log(`📋 Component: ${lookup.componentInfo.name} (${lookup.componentInfo.description})`);
      console.log(`🏷️ Category: ${lookup.categoryInfo.name} (${lookup.categoryInfo.description})`);
      console.log(`⏰ Generated: ${lookup.timestampInfo.date} (${lookup.timestampInfo.timeAgo})`);
      console.log('🔧 Troubleshooting:', lookup.troubleshooting);
    } else {
      console.warn('Invalid error code format. Expected: PM-COMPONENT-CATEGORY-TIMESTAMP-RANDOM');
    }
    
    console.groupEnd();
    return lookup;
  }

  /**
   * Replay initialization sequence with detailed logging
   */
  async replayInitialization(parrotId, options = {}) {
    console.group(`🔄 Replaying Initialization: ${parrotId || 'New Parrot'}`);
    
    const replayOptions = {
      enableDetailedLogging: true,
      simulateNetworkDelay: false,
      skipCache: false,
      maxRetries: 3,
      ...options
    };

    console.log('Replay Options:', replayOptions);
    
    const replay = {
      parrotId: parrotId || 'new',
      startTime: Date.now(),
      options: replayOptions,
      steps: [],
      success: false,
      duration: 0,
      errors: [],
      warnings: []
    };

    try {
      // Enable detailed logging
      const originalLogLevel = debugLogger.logLevel;
      if (replayOptions.enableDetailedLogging) {
        debugLogger.setLogLevel('debug');
      }

      // Simulate initialization steps
      console.log('🚀 Starting initialization replay...');
      
      // Step 1: Setup
      await this.replayStep('setup', 'Setting up initialization context', 200);
      
      // Step 2: Authentication
      await this.replayStep('auth', 'Validating authentication', 300);
      
      // Step 3: Data Loading
      if (parrotId) {
        await this.replayStep('data_load', 'Loading parrot data from API', 1500, replayOptions.simulateNetworkDelay);
      } else {
        await this.replayStep('defaults', 'Loading default configuration', 100);
      }
      
      // Step 4: Processing
      await this.replayStep('processing', 'Processing configuration data', 400);
      
      // Step 5: Finalization
      await this.replayStep('finalization', 'Finalizing initialization', 200);
      
      replay.success = true;
      console.log('✅ Initialization replay completed successfully');
      
      // Restore original log level
      if (replayOptions.enableDetailedLogging) {
        debugLogger.setLogLevel(originalLogLevel);
      }
      
    } catch (error) {
      replay.errors.push(error.message);
      console.error('❌ Initialization replay failed:', error);
    }

    replay.duration = Date.now() - replay.startTime;
    
    console.log('Replay Summary:', {
      success: replay.success,
      duration: `${replay.duration}ms`,
      steps: replay.steps.length,
      errors: replay.errors.length
    });
    
    console.groupEnd();
    return replay;
  }

  // Helper methods for new tools

  async simulateNormalFlow() {
    const steps = [];
    steps.push(await this.simulateStep('setup', 200));
    steps.push(await this.simulateStep('auth', 300));
    steps.push(await this.simulateStep('data_load', 1200));
    steps.push(await this.simulateStep('processing', 400));
    steps.push(await this.simulateStep('finalization', 200));
    return steps;
  }

  async simulateSlowNetworkFlow() {
    const steps = [];
    steps.push(await this.simulateStep('setup', 200));
    steps.push(await this.simulateStep('auth', 800));
    steps.push(await this.simulateStep('data_load', 4500));
    steps.push(await this.simulateStep('processing', 600));
    steps.push(await this.simulateStep('finalization', 300));
    return steps;
  }

  async simulateApiErrorFlow() {
    const steps = [];
    steps.push(await this.simulateStep('setup', 200));
    steps.push(await this.simulateStep('auth', 300));
    try {
      steps.push(await this.simulateStep('data_load', 1200, true)); // Force error
    } catch (error) {
      steps.push({ name: 'data_load', status: 'failed', duration: 1200, error: error.message });
    }
    return steps;
  }

  async simulateTimeoutFlow() {
    const steps = [];
    steps.push(await this.simulateStep('setup', 200));
    steps.push(await this.simulateStep('auth', 300));
    try {
      steps.push(await this.simulateStep('data_load', 30000, true)); // Simulate timeout
    } catch (error) {
      steps.push({ name: 'data_load', status: 'timeout', duration: 30000, error: 'Request timeout' });
    }
    return steps;
  }

  async simulateAuthErrorFlow() {
    const steps = [];
    steps.push(await this.simulateStep('setup', 200));
    try {
      steps.push(await this.simulateStep('auth', 300, true)); // Force auth error
    } catch (error) {
      steps.push({ name: 'auth', status: 'failed', duration: 300, error: 'Authentication failed' });
    }
    return steps;
  }

  async simulateStep(name, duration, shouldFail = false) {
    const startTime = Date.now();
    
    if (shouldFail) {
      throw new Error(`Simulated error in ${name} step`);
    }
    
    await new Promise(resolve => setTimeout(resolve, Math.min(duration, 100))); // Cap simulation time
    
    return {
      name,
      status: 'completed',
      duration: Date.now() - startTime,
      simulated: true
    };
  }

  async replayStep(name, description, baseDuration, simulateDelay = false) {
    console.log(`🔄 ${name}: ${description}`);
    
    const duration = simulateDelay ? baseDuration * 2 : Math.min(baseDuration, 100);
    await new Promise(resolve => setTimeout(resolve, duration));
    
    console.log(`✅ ${name} completed in ${duration}ms`);
    return { name, description, duration };
  }

  getComponentInfo(componentCode) {
    const components = {
      'PWS1': { name: 'ParrotWizardStep1', description: 'Parrot wizard step 1 component' },
      'API': { name: 'ApiClient', description: 'API client utility' },
      'PERF': { name: 'PerformanceMonitor', description: 'Performance monitoring utility' },
      'CACHE': { name: 'CacheManager', description: 'Cache management utility' },
      'AUTH': { name: 'Authentication', description: 'Authentication system' },
      'GEN': { name: 'General', description: 'General system error' }
    };
    
    return components[componentCode] || { name: 'Unknown', description: 'Unknown component' };
  }

  getCategoryInfo(categoryCode) {
    const categories = {
      'NETWORK': { name: 'Network Error', description: 'Network connectivity or request error' },
      'TIMEOUT': { name: 'Timeout Error', description: 'Request or operation timeout' },
      'AUTH': { name: 'Authentication Error', description: 'Authentication or authorization error' },
      'SERVER': { name: 'Server Error', description: 'Server-side error' },
      'CLIENT': { name: 'Client Error', description: 'Client-side error' },
      'VALIDATION': { name: 'Validation Error', description: 'Data validation error' },
      'PERFORMANCE': { name: 'Performance Issue', description: 'Performance-related issue' },
      'ERROR': { name: 'General Error', description: 'General error condition' }
    };
    
    return categories[categoryCode] || { name: 'Unknown', description: 'Unknown error category' };
  }

  getTimestampInfo(timestampCode) {
    try {
      const timestamp = parseInt(timestampCode, 36);
      const date = new Date(timestamp);
      const now = Date.now();
      const diff = now - timestamp;
      
      const timeAgo = diff < 60000 ? 'Less than a minute ago' :
                     diff < 3600000 ? `${Math.floor(diff / 60000)} minutes ago` :
                     diff < 86400000 ? `${Math.floor(diff / 3600000)} hours ago` :
                     `${Math.floor(diff / 86400000)} days ago`;
      
      return {
        timestamp,
        date: date.toISOString(),
        timeAgo,
        isRecent: diff < 3600000 // Within last hour
      };
    } catch (error) {
      return {
        timestamp: null,
        date: 'Invalid timestamp',
        timeAgo: 'Unknown',
        isRecent: false
      };
    }
  }

  getTroubleshootingInfo(componentCode, categoryCode) {
    const troubleshooting = {
      'PWS1-NETWORK': 'Check internet connection and try refreshing the page',
      'PWS1-TIMEOUT': 'The request took too long. Try again or check server status',
      'PWS1-AUTH': 'Sign out and sign back in to refresh your session',
      'PWS1-SERVER': 'Server error occurred. Try again in a few minutes',
      'API-NETWORK': 'Network connectivity issue. Check your connection',
      'API-TIMEOUT': 'API request timed out. Server may be under high load',
      'API-AUTH': 'Authentication failed. Please sign in again'
    };
    
    const key = `${componentCode}-${categoryCode}`;
    return troubleshooting[key] || 'Contact support with this error code for assistance';
  }

  findRelatedErrors(errorCode) {
    // In a real implementation, this would search through error logs
    // For now, return placeholder data
    return [
      'Similar errors in the last hour: 0',
      'This error pattern frequency: Low',
      'Suggested resolution success rate: 85%'
    ];
  }

  /**
   * Check API health
   */
  async checkApiHealth() {
    try {
      const healthCheck = await this.testApiEndpoint('/api/health', { timeout: 5000 });
      return {
        status: healthCheck.success ? 'healthy' : 'unhealthy',
        responseTime: healthCheck.duration,
        error: healthCheck.error
      };
    } catch (error) {
      return {
        status: 'error',
        error: error.message
      };
    }
  }

  /**
   * Check browser compatibility
   */
  checkBrowserCompatibility() {
    const features = {
      fetch: typeof fetch !== 'undefined',
      promises: typeof Promise !== 'undefined',
      localStorage: typeof localStorage !== 'undefined',
      sessionStorage: typeof sessionStorage !== 'undefined',
      webWorkers: typeof Worker !== 'undefined',
      performanceObserver: typeof PerformanceObserver !== 'undefined',
      intersectionObserver: typeof IntersectionObserver !== 'undefined'
    };

    const unsupportedFeatures = Object.entries(features)
      .filter(([, supported]) => !supported)
      .map(([feature]) => feature);

    return {
      features,
      unsupportedFeatures,
      isCompatible: unsupportedFeatures.length === 0
    };
  }

  /**
   * Check localStorage availability and usage
   */
  checkLocalStorage() {
    try {
      const testKey = 'parrotmate_test';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      
      const keys = Object.keys(localStorage);
      const parrotmateKeys = keys.filter(key => key.startsWith('parrotmate_'));
      
      return {
        available: true,
        totalKeys: keys.length,
        parrotmateKeys: parrotmateKeys.length,
        keys: parrotmateKeys
      };
    } catch (error) {
      return {
        available: false,
        error: error.message
      };
    }
  }

  /**
   * Check sessionStorage availability and usage
   */
  checkSessionStorage() {
    try {
      const testKey = 'parrotmate_test';
      sessionStorage.setItem(testKey, 'test');
      sessionStorage.removeItem(testKey);
      
      const keys = Object.keys(sessionStorage);
      const parrotmateKeys = keys.filter(key => key.startsWith('parrotmate_'));
      
      return {
        available: true,
        totalKeys: keys.length,
        parrotmateKeys: parrotmateKeys.length,
        keys: parrotmateKeys
      };
    } catch (error) {
      return {
        available: false,
        error: error.message
      };
    }
  }

  /**
   * Check network conditions
   */
  checkNetworkConditions() {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    
    return {
      online: navigator.onLine,
      connection: connection ? {
        effectiveType: connection.effectiveType,
        downlink: connection.downlink,
        rtt: connection.rtt,
        saveData: connection.saveData
      } : null
    };
  }

  /**
   * Identify issues from diagnostics
   */
  identifyIssues(diagnostics) {
    const issues = [];

    // Performance issues
    if (diagnostics.performance.summary.averageResponseTime > 3000) {
      issues.push({
        type: 'performance',
        severity: 'warning',
        message: 'Slow average response time',
        value: diagnostics.performance.summary.averageResponseTime
      });
    }

    // Error rate issues
    if (diagnostics.performance.summary.errorRate > 5) {
      issues.push({
        type: 'reliability',
        severity: 'critical',
        message: 'High error rate',
        value: diagnostics.performance.summary.errorRate
      });
    }

    // Browser compatibility issues
    if (!diagnostics.browserCompatibility.isCompatible) {
      issues.push({
        type: 'compatibility',
        severity: 'critical',
        message: 'Browser compatibility issues',
        value: diagnostics.browserCompatibility.unsupportedFeatures
      });
    }

    // Storage issues
    if (!diagnostics.localStorage.available) {
      issues.push({
        type: 'storage',
        severity: 'warning',
        message: 'localStorage not available',
        value: diagnostics.localStorage.error
      });
    }

    return issues;
  }

  /**
   * Generate recommendations based on issues
   */
  generateRecommendations(issues) {
    const recommendations = [];

    issues.forEach(issue => {
      switch (issue.type) {
        case 'performance':
          recommendations.push('Consider implementing request caching and optimization');
          break;
        case 'reliability':
          recommendations.push('Review error handling and implement better retry logic');
          break;
        case 'compatibility':
          recommendations.push('Add polyfills for unsupported browser features');
          break;
        case 'storage':
          recommendations.push('Implement fallback storage mechanisms');
          break;
      }
    });

    return [...new Set(recommendations)]; // Remove duplicates
  }

  /**
   * Calculate performance score
   */
  calculatePerformanceScore(performance) {
    let score = 100;
    
    // Deduct points for slow response times
    if (performance.summary.averageResponseTime > 1000) {
      score -= Math.min(30, (performance.summary.averageResponseTime - 1000) / 100);
    }
    
    // Deduct points for high error rate
    score -= Math.min(40, performance.summary.errorRate * 2);
    
    // Deduct points for low cache hit rate
    if (performance.summary.cacheHitRate < 50) {
      score -= (50 - performance.summary.cacheHitRate) / 2;
    }
    
    return Math.max(0, Math.round(score));
  }

  /**
   * Calculate overall health score
   */
  calculateHealthScore(diagnostics) {
    let score = 100;
    
    // Deduct points for each critical issue
    const criticalIssues = this.identifyIssues(diagnostics).filter(i => i.severity === 'critical');
    score -= criticalIssues.length * 20;
    
    // Deduct points for warning issues
    const warningIssues = this.identifyIssues(diagnostics).filter(i => i.severity === 'warning');
    score -= warningIssues.length * 10;
    
    // Factor in performance score
    const performanceScore = this.calculatePerformanceScore(diagnostics.performance);
    score = (score + performanceScore) / 2;
    
    return Math.max(0, Math.round(score));
  }

  /**
   * Enable development tools
   */
  enableDevelopmentTools() {
    localStorage.setItem('parrotmate_dev_tools', 'true');
    this.isEnabled = true;
    this.initializeDevelopmentTools();
    
    debugLogger.info('Development tools enabled');
    console.log('🛠️ ParrotMate Development Tools Enabled');
  }

  /**
   * Disable development tools
   */
  disableDevelopmentTools() {
    localStorage.removeItem('parrotmate_dev_tools');
    this.isEnabled = false;
    
    // Remove global interface
    if (window.parrotmateDev) {
      delete window.parrotmateDev;
    }
    
    debugLogger.info('Development tools disabled');
    console.log('🛠️ ParrotMate Development Tools Disabled');
  }

  /**
   * Reset development tools
   */
  resetDevelopmentTools() {
    this.tools.clear();
    this.diagnostics.clear();
    
    if (this.isEnabled) {
      this.initializeDevelopmentTools();
    }
    
    debugLogger.info('Development tools reset');
    console.log('🛠️ ParrotMate Development Tools Reset');
  }
}

// Create singleton instance
const developmentTools = new DevelopmentTools();

export default developmentTools;
